import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of, delay, map } from 'rxjs';
import { Announcement } from '../models/announcement.model';
import { environment } from '../../../environments/environment';
import { MOCK_LISTING_DETAILS } from '../mocks/mock-listing-details';
import { MOCK_SIMILAR_LISTINGS } from '../mocks/mock-similar-listings';
import { Complaint, MOCK_COMPLAINTS } from '../mocks/mock-complaints';
import { CACHE_ENABLED } from '../interceptors/caching.interceptor';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AnnouncementService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  private listings: Announcement[] = MOCK_LISTING_DETAILS;
  private similarListings: Announcement[] = MOCK_SIMILAR_LISTINGS;
  private complaints: Complaint[] = MOCK_COMPLAINTS;

  getAnnouncementById(id: string | number): Observable<Announcement | undefined> {
    return this.http.get<ApiResponse<Announcement>>(`${this.apiUrl}/Listings/public/${id}`, {
      context: new HttpContext().set(CACHE_ENABLED, true)
    }).pipe(
      map(response => {
        if (response.success && response.data) {
          const data = response.data;
          // Map API fields to UI-expected legacy fields if they don't match
          return {
            ...data,
            rooms: data.roomCount ?? 0,
            area_m2: data.areaSquareMeters ?? 0,
            total_floors: data.totalFloors ?? 0,
            hasExtract: data.hasDocument,
            hasMortgage: data.isMortgageAvailable,
            lat: data.latitude,
            lng: data.longitude,
            imageUrls: (data.images && Array.isArray(data.images))
              ? data.images.map((img: any) => img.url || img)
              : (data.imageUrls || [])
          } as Announcement;
        }
        return undefined;
      })
    );
  }

  getSimilarListings(id: string | number): Observable<Announcement[]> {
    return of(this.similarListings).pipe(delay(500));
  }

  reportAnnouncement(listingId: string | number, reasonId: number | string, comment?: string): Observable<boolean> {
    const url = `${this.apiUrl}/Complaint`;
    return this.http.post<ApiResponse<any>>(url, {
      listingId: Number(listingId),
      complaintReasonId: Number(reasonId),
      text: comment
    }).pipe(
      map(res => res.success)
    );
  }

  upgradeToVip(id: string | number, packageId: string): Observable<boolean> {
    const announcement = this.listings.find(a => a.id.toString() === id.toString());
    if (announcement) {
      announcement.isVip = true;
      // In a real app, you'd calculate vipExpiresAt based on packageId
      return of(true).pipe(delay(1000));
    }
    return of(false).pipe(delay(1000));
  }
}
